import type { BetaFeatureCompanySummary, BetaFeatureListing } from 'types';
import type { AdminBetaFeaturesManager } from '@/state/useAdminBetaFeatures';
import { useState } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { companyReach, enableBlockedReason } from '@/state/useAdminBetaFeatures';

type CompanyPickerProps = { listing: BetaFeatureListing; manager: AdminBetaFeaturesManager };

/**
 * The add control of one feature: search companies by name or id and pick one to switch the
 * feature on for. A company already on, or Pertento's own (enableBlockedReason), shows but cannot
 * be picked; an agency is confirmed first, since its switch reaches every client. Built on Popover and Command directly, as ExperimentPicker is: the shared
 * Combobox cannot select anything while its value is empty, and filters on the client, where
 * this asks the server.
 */
export const CompanyPicker = ({ listing, manager }: CompanyPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { search, setSearch, searchResults, isSearching, isEnabling } = manager;
  const enabledIds = new Set(listing.companies.map((company) => company.id));

  const openChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSearch('');
  };

  const choose = (company: BetaFeatureCompanySummary) => {
    setIsOpen(false);
    manager.chooseCompany({ listing, company });
  };

  return (
    <Popover open={isOpen} onOpenChange={openChange}>
      <PopoverTrigger asChild>
        <Button size="sm" className="h-8 rounded-lg text-body" disabled={isEnabling}>
          Add company
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="border-line bg-panel w-80 rounded-lg p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or ID"
            value={search}
            onValueChange={setSearch}
            className="text-body"
          />
          <CommandList className="max-h-80">
            <CommandEmpty className="supporting px-3 py-4">
              {!search.trim() ? 'Type a company name or ID.' : isSearching ? 'Searching...' : 'No companies match.'}
            </CommandEmpty>
            {searchResults.length > 0 && (
              <CommandGroup>
                {searchResults.map((company) => {
                  const isOn = enabledIds.has(company.id);
                  const blockedReason = enableBlockedReason(company);
                  const note = blockedReason ?? companyReach(company);
                  return (
                    <CommandItem
                      key={company.id}
                      value={String(company.id)}
                      disabled={isOn || !!blockedReason}
                      onSelect={() => choose(company)}
                      className="cursor-pointer flex-col items-start gap-0.5 text-body"
                    >
                      <span className="flex w-full items-center gap-2">
                        <span className="min-w-0 flex-1 truncate font-medium">{company.name}</span>
                        <span className="micro-label shrink-0 tracking-normal">{isOn ? 'On' : `#${company.id}`}</span>
                      </span>
                      {note && <span className="supporting">{note}</span>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
